import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * How many children of a wrapping row still sit on its FIRST line.
 *
 * The row keeps every child mounted and clips the wrapped ones with
 * `overflow-hidden`, so the measurement never moves what it is measuring: the
 * off-line children are still laid out, their offsets stay valid, and the count
 * settles in one pass instead of oscillating the way a mount/unmount loop does.
 *
 * `offsetTop` is compared child-to-child inside one offset parent — layout
 * pixels on both sides — so the root `zoom` cancels out and no correction is
 * needed here.
 */
export const useRowOverflow = (
  itemCount: number,
): { rowRef: RefObject<HTMLDivElement | null>; visibleCount: number } => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const items = Array.from(row.children) as HTMLElement[];
    if (items.length === 0) {
      setVisibleCount(0);
      return;
    }
    const firstLineTop = items[0].offsetTop;
    setVisibleCount(items.filter((item) => item.offsetTop <= firstLineTop).length);
  }, []);

  useLayoutEffect(measure, [measure, itemCount]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [measure]);

  return { rowRef, visibleCount };
};

export default useRowOverflow;
